// ============================================================
// CLUBHUB – Gruppen: die Ansicht in der Adresse
// ============================================================
// Wie `lib/mitglieder/ansicht.ts`: Suche, Filter und Sortierung
// stehen in den searchParams — jede Ansicht ist ein Lesezeichen.
// Die Seite liest, der Filterkopf schreibt (`useAnsicht`).
// ============================================================
import { GroupKind } from "@/generated/prisma/enums";
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";

type Frage = Record<string, string | string[] | undefined>;

function eins(p: Frage, name: string): string {
  const w = p[name];
  return ((Array.isArray(w) ? w[0] : w) ?? "").trim();
}

// ── Gruppenbaum (/gruppen) ──────────────────────────────────

export const GRUPPEN_SORT = {
  gruppe: { label: "Gruppe", first: "auf" },
  art: { label: "Art", first: "auf" },
  mitglieder: { label: "Mitglieder", first: "ab" },
} as const satisfies SortColumns<string>;
export type GruppenSortKey = keyof typeof GRUPPEN_SORT;
export const GRUPPEN_DEFAULT_SORT: GruppenSortKey = "gruppe";

export type GruppenAnsicht = {
  q: string;
  /** Nur diese Art; `null` = alle. */
  art: GroupKind | null;
  /** Archivierte statt aktive Gruppen. */
  archiv: boolean;
  sort: SortState<GruppenSortKey>;
};

export const GRUPPEN_PARAMS = ["q", "art", "zustand", "sortierung", "richtung"] as const;

const IST_ART = new Set<string>(Object.values(GroupKind));

export function gruppenAnsichtLesen(p: Frage): GruppenAnsicht {
  const art = eins(p, "art").toUpperCase();
  return {
    q: eins(p, "q"),
    art: IST_ART.has(art) ? (art as GroupKind) : null,
    archiv: eins(p, "zustand") === "archiv",
    sort: readSort(p, GRUPPEN_SORT, GRUPPEN_DEFAULT_SORT),
  };
}

export function gruppenFilterZahl(a: GruppenAnsicht): number {
  return [a.q, a.art, a.archiv].filter(Boolean).length;
}

// ── Mitglieder einer Gruppe (/gruppen/[id]) ─────────────────

export const MITGLIEDER_SORT = {
  funktion: { label: "Funktion", first: "auf" },
  name: { label: "Name", first: "auf" },
  seit: { label: "Seit", first: "ab" },
  bis: { label: "Bis", first: "ab" },
} as const satisfies SortColumns<string>;
export type MitgliederSortKey = keyof typeof MITGLIEDER_SORT;
export const MITGLIEDER_DEFAULT_SORT: MitgliederSortKey = "funktion";

export const ZEITRAEUME = [
  { schluessel: "aktuell", label: "Aktuell", hinweis: "Wer heute in der Gruppe ist, dazu künftige Zugänge" },
  { schluessel: "frueher", label: "Früher", hinweis: "Beendete Zugehörigkeiten — die Historie der Gruppe" },
  { schluessel: "alle", label: "Alle", hinweis: "Jeder Zeitraum jeder Person" },
] as const;
export type Zeitraum = (typeof ZEITRAEUME)[number]["schluessel"];

export type MitgliederAnsicht = {
  q: string;
  zeitraum: Zeitraum;
  /** Nur Personen mit Funktion. */
  nurFunktion: boolean;
  /** Nur Personen ohne Foto-Einwilligung (Datenschutz). */
  ohneFoto: boolean;
  sort: SortState<MitgliederSortKey>;
};

export const MITGLIEDER_PARAMS = ["q", "zeitraum", "funktion", "foto", "sortierung", "richtung"] as const;

export function mitgliederAnsichtLesen(p: Frage): MitgliederAnsicht {
  const z = eins(p, "zeitraum");
  return {
    q: eins(p, "q"),
    zeitraum: z === "frueher" || z === "alle" ? z : "aktuell",
    nurFunktion: eins(p, "funktion") === "1",
    ohneFoto: eins(p, "foto") === "ohne",
    sort: readSort(p, MITGLIEDER_SORT, MITGLIEDER_DEFAULT_SORT),
  };
}

export function mitgliederFilterZahl(a: MitgliederAnsicht): number {
  return [a.q, a.zeitraum !== "aktuell", a.nurFunktion, a.ohneFoto].filter(Boolean).length;
}
